import { useCallback, useState, useEffect, useRef } from "react";
import { useEmployeeJobStore, JobStatus } from "@/modules/store/employeeStore";
import { CandidateAppliedJob } from "@/modules/types/job/jobApply";
import {
  ApplicantStatus,
  SortByJobField,
  SortType,
} from "@/modules/types/enum";
import { jobService } from "@/modules/services/jobService";
import { useEmployeeInfo } from "./useEmployeeInfo";
import { AppError } from "@/lib/utils/errors";
import { enqueueSnackbar } from "notistack";
import { Job } from "@/modules/types/job/job";

// Map JobStatus to ApplicantStatus for API calls
const JOB_STATUS_TO_APPLICANT_STATUS: Record<
  JobStatus,
  ApplicantStatus | undefined
> = {
  applied: undefined, // All applied jobs
  saved: undefined, // Saved jobs don't have applicant status
  interview: ApplicantStatus.considering,
  offered: ApplicantStatus.offered,
  rejected: ApplicantStatus.rejected,
  hired: ApplicantStatus.hired,
  expired: undefined, // Expired jobs
};

// Utility function to convert Job to CandidateAppliedJob
const convertJobToCandidateAppliedJob = (
  job: Job,
  employeeId: string
): CandidateAppliedJob => {
  return {
    id: `saved_${job.id}`, // Create a unique ID for saved jobs
    jobId: job.id,
    jobSlug: job.slug,
    employeeId: employeeId,
    areYouInHighlightGroup: false,
    isArchived: false,
    status: ApplicantStatus.pending, // Use pending status for saved jobs since there's no 'saved' status
    totalBusinessViews: job.totalViews || 0,
    statusUpdatedAt: job.updatedAt || job.createdAt || Date.now(),
    competitionRatio: job.totalApplicants || 0,
    companyLogo: job.companyInfo?.companyLogo || "",
    companyName: job.companyInfo?.companyName || "",
    jobTitle: job.jobTitle,
    jobType: job.jobType,
    province: job.province || "",
    district: job.district || "",
    appliedAt: job.createdAt || Date.now(), // Use job creation date as saved date
    minSalary: job.minSalary,
    maxSalary: job.maxSalary,
    moneyUnit: job.moneyUnit,
    isNegotiable: job.isNegotiable,
    level: job.level || { id: "", title: "", order: 0 },
  };
};

// Global flag to prevent multiple simultaneous API calls
let isFetching = false;

export function useCandidateJob(
  selectedJobId?: string,
  selectedApplyId?: string,
  initialStatus?: JobStatus
) {
  const [status, setStatus] = useState<JobStatus>(initialStatus || "applied");
  const [sortBy, setSortBy] = useState<SortByJobField>(
    SortByJobField.createdAt
  );
  const [sortType, setSortType] = useState<SortType>(SortType.desc);
  const [totalCounts, setTotalCounts] = useState<Record<JobStatus, number>>({
    applied: 0,
    saved: 0,
    offered: 0,
    hired: 0,
    rejected: 0,
    interview: 0,
    expired: 0,
  });
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const { employeeInfo } = useEmployeeInfo();
  const appliedJobStatusMap = useEmployeeJobStore((s) => s.appliedJobStatusMap);
  const setAppliedJobs = useEmployeeJobStore((s) => s.setAppliedJobs);
  const setTotal = useEmployeeJobStore((s) => s.setTotal);
  const setIsLoading = useEmployeeJobStore((s) => s.setIsLoading);
  const setPageStore = useEmployeeJobStore((s) => s.setPage);
  const setLimitStore = useEmployeeJobStore((s) => s.setLimit);
  const clearJobs = useEmployeeJobStore((s) => s.clearJobs);

  const current = appliedJobStatusMap[status];

  // Use ref to access current state without causing re-renders
  const appliedJobStatusMapRef = useRef(appliedJobStatusMap);
  appliedJobStatusMapRef.current = appliedJobStatusMap;

  // Fetch total counts for all job statuses
  const fetchTotalCounts = useCallback(async () => {
    if (!employeeInfo?.id) {
      console.warn("No employee info available for fetching total counts");
      return;
    }

    setIsLoadingCounts(true);
    try {
      const response = await jobService.getTotalAllJob(employeeInfo.id);

      if (response) {
        const newCounts: Record<JobStatus, number> = {
          applied: response.totalApplied || 0,
          saved: response.totalSaved || 0,
          offered: response.totalOffer || 0,
          hired: response.totalHired || 0,
          rejected: response.totalRejected || 0,
          interview: 0, // Interview jobs are part of applied, will be calculated separately
          expired: 0, // Expired jobs are part of applied, will be calculated separately
        };

        setTotalCounts(newCounts);

        // Update the store totals with the fetched counts
        Object.entries(newCounts).forEach(([statusKey, count]) => {
          const jobStatus = statusKey as JobStatus;
          setTotal(jobStatus, count);
        });
      }
    } catch (error) {
      const appErr = new AppError(error);
      appErr.setAction("useCandidateJob.fetchTotalCounts");
      appErr.setApiAction("jobService.getJobApplies");
      appErr.logError();
      console.warn("Failed to fetch total job counts:", error);
    } finally {
      setIsLoadingCounts(false);
    }
  }, [employeeInfo?.id, setTotal]);

  const fetchJobs = useCallback(
    async (override?: {
      status?: JobStatus;
      page?: number;
      limit?: number;
      sortBy?: SortByJobField;
      sortType?: SortType;
    }) => {
      if (!employeeInfo?.id) {
        console.warn("No employee info available");
        return;
      }

      // Prevent multiple simultaneous API calls
      if (isFetching) {
        console.warn("Already fetching jobs, skipping...");
        return;
      }

      isFetching = true;
      const s = override?.status ?? status;
      const currentStatusMap = appliedJobStatusMapRef.current[s];
      const page = override?.page ?? currentStatusMap.page;
      const limit = override?.limit ?? currentStatusMap.limit;
      const sortByParam = override?.sortBy ?? sortBy;
      const sortTypeParam = override?.sortType ?? sortType;

      setIsLoading(s, true);

      try {
        let appliedJobs: CandidateAppliedJob[] = [];
        let total = 0;

        if (s === "applied") {
          // Fetch applied jobs
          const applicantStatus = JOB_STATUS_TO_APPLICANT_STATUS[s];
          const response = await jobService.getJobApplies(
            employeeInfo.id,
            page,
            limit,
            undefined,
            selectedJobId,
            applicantStatus,
            sortByParam,
            sortTypeParam,
            selectedApplyId
          );

          if (response && response.jobs) {
            appliedJobs = response.jobs;
            total = response.total;
          }
        } else if (s === "saved") {
          // Fetch saved jobs
          const response = await jobService.getSavedJobs(employeeInfo.id);

          if (response && response.jobs) {
            // Convert Job objects to CandidateAppliedJob format and deduplicate by jobId
            const seenJobIds = new Set<string>();
            const uniqueJobs = response.jobs.filter((job) => {
              if (seenJobIds.has(job.id)) {
                return false;
              }
              seenJobIds.add(job.id);
              return true;
            });

            appliedJobs = uniqueJobs.map((job: Job) =>
              convertJobToCandidateAppliedJob(job, employeeInfo.id)
            );
            total = uniqueJobs.length; // Use the deduplicated count
          }
        } else {
          // For other statuses, filter applied jobs by status
          const applicantStatus = JOB_STATUS_TO_APPLICANT_STATUS[s];
          if (applicantStatus) {
            const response = await jobService.getJobApplies(
              employeeInfo.id,
              page,
              limit,
              undefined,
              selectedJobId,
              applicantStatus,
              sortByParam,
              sortTypeParam,
              selectedApplyId
            );

            if (response && response.jobs) {
              appliedJobs = response.jobs;
              total = response.total;
            }
          }
        }

        setAppliedJobs(s, appliedJobs);
        setTotal(s, total);
      } catch (error) {
        const appErr = new AppError(error);
        appErr.setAction("useCandidateJob.fetchJobs");
        appErr.setApiAction("jobService.getJobApplies/getSavedJobs");
        appErr.logError();
        enqueueSnackbar("Failed to fetch jobs", { variant: "error" });
      } finally {
        setIsLoading(s, false);
        isFetching = false;
      }
    },
    [
      status,
      sortBy,
      sortType,
      setIsLoading,
      setAppliedJobs,
      setTotal,
      employeeInfo?.id,
      selectedJobId,
      selectedApplyId,
    ]
  );

  // Clear previous data when switching tabs
  const handleTabChange = (newStatus: JobStatus) => {
    setStatus(newStatus);
    // Clear previous data to prevent accumulation
    clearJobs(newStatus);
    // Fetch if jobs are not loaded
    if (appliedJobStatusMap[newStatus].appliedJobs.length === 0) {
      fetchJobs({ status: newStatus });
    }
  };

  const setPage = (page: number) => {
    setPageStore(status, page);
    fetchJobs({ page });
  };

  const setLimit = (limit: number) => {
    setLimitStore(status, limit);
    fetchJobs({ limit });
  };

  const handleSortChange = (
    newSortBy: SortByJobField,
    newSortType: SortType
  ) => {
    setSortBy(newSortBy);
    setSortType(newSortType);
    // Clear current data and fetch with new sort
    clearJobs(status);
    fetchJobs({ status, sortBy: newSortBy, sortType: newSortType });
  };

  // Sync initialStatus to status state when it changes
  useEffect(() => {
    if (initialStatus && initialStatus !== status) {
      setStatus(initialStatus);
    }
  }, [initialStatus]);

  // Fetch total counts when employee info is available
  useEffect(() => {
    if (employeeInfo?.id) {
      fetchTotalCounts();
    }
  }, [employeeInfo?.id, fetchTotalCounts]);

  // Fetch jobs when component mounts or employee info changes
  useEffect(() => {
    if (employeeInfo?.id) {
      // Only fetch if we don't have data for the current status
      const currentStatusData = appliedJobStatusMap[status];
      if (
        currentStatusData.appliedJobs.length === 0 &&
        !currentStatusData.isLoading
      ) {
        fetchJobs();
      }
    }
  }, [employeeInfo?.id, status, selectedJobId, selectedApplyId]);

  // For tab counts - use the fetched total counts, fallback to store counts
  const counts: Record<JobStatus, number> = Object.fromEntries(
    Object.entries(appliedJobStatusMap).map(([k, v]) => [
      k,
      totalCounts[k as JobStatus] || v.total,
    ])
  ) as Record<JobStatus, number>;

  return {
    status,
    setStatus: handleTabChange,
    appliedJobs: current.appliedJobs,
    count: counts[status],
    page: current.page,
    limit: current.limit,
    total: current.total,
    sortBy,
    sortType,
    setPage,
    setLimit,
    setSortBy,
    setSortType,
    handleSortChange,
    isLoading: current.isLoading,
    isLoadingCounts,
    fetchJobs,
    fetchTotalCounts,
    counts, // all tab counts with fetched totals
  };
}

/**
 * Hook for changing application status and updating the store.
 * Action methods accept only jobApplyId (string), currentStatus (ApplicantStatus), and optionally the old job object for store update.
 */
export const useStatusJobApplyChange = () => {
  const { employeeInfo } = useEmployeeInfo();
  const updateAppliedJob = useEmployeeJobStore((s) => s.updateAppliedJob);
  const appliedJobStatusMap = useEmployeeJobStore((s) => s.appliedJobStatusMap);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Helper to find the current status tab for a job
  const findJobStatus = (jobApplyId: string): JobStatus | null => {
    for (const status of Object.keys(appliedJobStatusMap) as JobStatus[]) {
      if (
        appliedJobStatusMap[status].appliedJobs.some((j) => j.id === jobApplyId)
      ) {
        return status;
      }
    }
    return null;
  };

  // Helper to get the current CandidateAppliedJob object
  const getAppliedJob = (jobApplyId: string): CandidateAppliedJob | null => {
    for (const status of Object.keys(appliedJobStatusMap) as JobStatus[]) {
      const found = appliedJobStatusMap[status].appliedJobs.find(
        (j) => j.id === jobApplyId
      );
      if (found) return found;
    }
    return null;
  };

  // Generic handler
  const handleStatusChange = async (
    action: "acceptExchange" | "cancelExchange" | "denyOffer" | "acceptOffer",
    jobApplyId: string,
    currentStatus: ApplicantStatus
  ) => {
    if (!employeeInfo?.id) {
      setError("No employee info");
      enqueueSnackbar("No employee info", { variant: "error" });
      return;
    }
    setLoading(action);
    setError(null);
    try {
      let apiResp;
      let newStatus: ApplicantStatus | null = null;
      switch (action) {
        case "acceptExchange":
          apiResp = await jobService.acceptExchange(
            jobApplyId,
            employeeInfo.id
          );
          newStatus = ApplicantStatus.exchange;
          break;
        case "cancelExchange":
          apiResp = await jobService.cancelExchange(
            jobApplyId,
            employeeInfo.id
          );
          newStatus = ApplicantStatus.canceled;
          break;
        case "denyOffer":
          apiResp = await jobService.denyOffer(jobApplyId, employeeInfo.id);
          newStatus = ApplicantStatus.denied;
          break;
        case "acceptOffer":
          apiResp = await jobService.acceptOffer(jobApplyId, employeeInfo.id);
          newStatus = ApplicantStatus.hired;
          break;
      }
      if (apiResp && newStatus) {
        // Update the job in the store
        const statusTab = findJobStatus(jobApplyId) || "applied";
        const oldJob = getAppliedJob(jobApplyId);
        if (oldJob) {
          updateAppliedJob(statusTab, {
            ...oldJob,
            status: newStatus,
            statusUpdatedAt: Date.now(),
          });
        }
        enqueueSnackbar("Status updated successfully", { variant: "success" });
      } else {
        throw new Error(apiResp?.message || "Failed to update status");
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction(`useStatusJobApplyChange.handleStatusChange[${action}]`);
      appErr.setApiAction(`jobService.${action}`);
      appErr.logError(
        `useStatusJobApplyChange Error From status: ${currentStatus} to ${action}`
      );
      setError(appErr.message);
      enqueueSnackbar(appErr.message, { variant: "error" });
    } finally {
      setLoading(null);
    }
  };

  return {
    loading,
    error,
    /**
     * Accept exchange action
     * @param jobApplyId string
     * @param currentStatus ApplicantStatus
     */
    acceptExchange: (jobApplyId: string, currentStatus: ApplicantStatus) =>
      handleStatusChange("acceptExchange", jobApplyId, currentStatus),
    /**
     * Cancel exchange action
     * @param jobApplyId string
     * @param currentStatus ApplicantStatus
     */
    cancelExchange: (jobApplyId: string, currentStatus: ApplicantStatus) =>
      handleStatusChange("cancelExchange", jobApplyId, currentStatus),
    /**
     * Deny offer action
     * @param jobApplyId string
     * @param currentStatus ApplicantStatus
     */
    denyOffer: (jobApplyId: string, currentStatus: ApplicantStatus) =>
      handleStatusChange("denyOffer", jobApplyId, currentStatus),
    /**
     * Accept offer action
     * @param jobApplyId string
     * @param currentStatus ApplicantStatus
     */
    acceptOffer: (jobApplyId: string, currentStatus: ApplicantStatus) =>
      handleStatusChange("acceptOffer", jobApplyId, currentStatus),
  };
};
